import React, { useEffect, useState } from "react";
import { getSports, type PlatformSport } from "../services/sports";

/**
 * Sports, shown as names.
 *
 * `clubs.sports` and a coach's certifications hold sport IDs — they used to
 * hold names, and some old rows still do. Printed raw, an admin saw a UUID
 * where a sport should be. This resolves an id to its name and leaves anything
 * it does not recognise alone, so the legacy rows keep reading correctly.
 *
 * The catalogue is fetched once per page load and shared: several of these
 * appear on the same screen, and none of them needs its own request.
 */

let cache: PlatformSport[] | null = null;
let inFlight: Promise<PlatformSport[]> | null = null;

function loadCatalogue(): Promise<PlatformSport[]> {
  if (cache) return Promise.resolve(cache);
  if (!inFlight) {
    inFlight = getSports(true)
      .then((list) => {
        cache = list;
        return list;
      })
      .catch(() => [])
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

/** Exposed for screens that need the names as text rather than as chips. */
export function useSportNames(): (value: string) => string {
  const [byId, setById] = useState<Record<string, string>>({});

  useEffect(() => {
    let alive = true;
    loadCatalogue().then((list) => {
      if (!alive) return;
      setById(Object.fromEntries(list.map((s) => [s.id, s.name])));
    });
    return () => {
      alive = false;
    };
  }, []);

  return (value: string) => byId[value] || value;
}

export default function SportChips({
  values,
  className = "",
  chipClassName = "px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-xs sm:text-sm border border-indigo-200",
  empty = "—",
}: {
  values: string[] | null | undefined;
  className?: string;
  chipClassName?: string;
  empty?: React.ReactNode;
}) {
  const nameOf = useSportNames();
  const list = (values || []).filter(Boolean);

  if (list.length === 0) return <span className="text-slate-400">{empty}</span>;

  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {list.map((value) => (
        <span key={value} className={chipClassName}>
          {nameOf(value)}
        </span>
      ))}
    </div>
  );
}
